import React, { useCallback, useEffect, useState } from 'react'
type Props = {
  children: React.ReactNode
  className?: string
  hideLoadingLabel?: boolean
  loadingNode?: React.ReactNode
  loadMoreNode?: React.ReactNode
  loadNext: (next: string) => void
  next: string | null | undefined
}

/**
 * A manual scroll component that provides a "Load more" button for paginated content.
 * Unlike infinite scroll, this requires user interaction to load additional content.
 *
 * @param children - The content to display that has been loaded
 * @param className - Optional CSS class name for the load more button container
 * @param hideLoadingLabel - Whether to hide the loading label (unused in this implementation)
 * @param loadingNode - The content to display while loading more content (default: 'Loading...')
 * @param loadMoreNode - The content to display in the load more button (default: 'Load more...')
 * @param loadNext - Callback function to load the next batch of content
 * @param next - The next page token/URL or null if no more content to load
 * @returns The content with a load more button when more content is available
 */
const ManualScroll = ({
  children,
  className,
  loadingNode = 'Loading...',
  loadMoreNode = 'Load more...',
  loadNext,
  next,
}: Props): React.ReactNode => {
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    setLoading(false)
  }, [next])
  const loadMore = useCallback(() => {
    if (loading || !next || !loadNext) return
    setLoading(true)
    loadNext(next)
  }, [loading, loadNext, next])
  return (
    <>
      {children}
      {next && (
        <div
          className={className}
          onClick={!loading && Boolean(next) ? loadMore : undefined}
        >
          {loading && loadingNode}
          {!loading && Boolean(next) && loadMoreNode}
        </div>
      )}
    </>
  )
}

export default ManualScroll
